/**
 * "This is a public or shared computer", for as long as this tab is open.
 *
 * The daemon's half is a short session and a cookie that dies with the browser.
 * This is the client's half, and it is a different promise: **while this flag is
 * set, nothing in the app writes `localStorage`, and nothing reads it either.**
 *
 * One rule, stated once here and referenced from the half-dozen places that
 * enforce it, because a per-key judgement is how a rule like this rots. Every
 * one of those keys was defensible on its own -- a theme, a column width, a
 * crash guard, a push registration id -- and the argument for keeping each of
 * them is the same argument, made by somebody who was not thinking about the
 * machine in the library.
 *
 * # Why reads are gated too, and not only writes
 *
 * Because the storage on a shared machine belongs to whoever used it last.
 * A session that has promised to leave nothing behind should not begin by
 * picking up a stranger's preferences, a stranger's open tabs, or -- the one
 * that actually matters -- a stranger's half-written message. Refusing to read
 * costs a public session its defaults and nothing else.
 *
 * # Why `sessionStorage`
 *
 * It has exactly the lifetime this mode claims to have. It dies when the tab is
 * closed, which is what "the session ends when the browser closes" means from
 * the page's side, and it survives a reload of that tab -- which `localStorage`
 * would too, and a module variable would not. A reload is not a change of mind,
 * and a mode that silently switched itself off on F5 would be worse than not
 * offering it, because the person would have no way to tell.
 *
 * Storing the flag in a place the mode is otherwise refusing to use is not a
 * contradiction: `sessionStorage` is per tab and goes away by itself. The whole
 * problem with `localStorage` is that it does not.
 */

/**
 * The key. Deliberately not `hm.`-prefixed like a setting is -- it is not one,
 * and nothing that sweeps preferences should find it.
 */
const KEY = "hm.public-session";

/**
 * The same answer, for a browser that refuses storage entirely.
 *
 * Private-browsing modes throw on `sessionStorage` access rather than returning
 * null, and a `catch` that answered "not a public session" would silently
 * disable this whole feature on exactly the browsers whose users are most
 * likely to have asked for it. So the flag is held here as well: the module
 * variable is what survives a broken storage, and the storage is what survives
 * a reload. Neither can do the other's job.
 */
let marked = false;

export function isPublicSession(): boolean {
  if (marked) return true;
  try {
    return sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function markPublicSession(): void {
  marked = true;
  try {
    sessionStorage.setItem(KEY, "1");
  } catch {
    // See {@link marked}. The mode still holds for this page; it just will not
    // survive a reload, which is the lesser of the two failures available.
  }
}

export function clearPublicSession(): void {
  marked = false;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* as above */
  }
}
